import { useEffect, useState } from 'react';
import { useSetAtom } from 'jotai';
import { FileText, User, Briefcase, ArrowUpRightSquare } from 'lucide-react';
import { Card } from '../ui/card';
import { clsx } from 'clsx';
import type { AssignmentResponse } from '@/types/api';
import { fetchSourceByIdActionAtom } from '@/stores/sourcesStore';
import { fetchTipByIdActionAtom } from '@/stores/tipsStore';
import { SourceResponse } from '@/types/sources';
import { TipResponse } from '@/types/tips';

interface AssignmentRelatedInfoProps {
  assignment: AssignmentResponse;
  className: string;
}

export function AssignmentRelatedInfo({
  assignment,
  className,
}: AssignmentRelatedInfoProps) {
  const fetchSourceById = useSetAtom(fetchSourceByIdActionAtom);
  const fetchTipById = useSetAtom(fetchTipByIdActionAtom);

  const [source, setSource] = useState<SourceResponse | null>(null);
  const [tip, setTip] = useState<TipResponse | null>(null);

  useEffect(() => {
    if (assignment.sourceId) {
      fetchSourceById(assignment.sourceId)
        .then(result => setSource(result))
        .catch(console.error);
    }
  }, [assignment.sourceId, fetchSourceById]);

  useEffect(() => {
    if (assignment.tipId) {
      fetchTipById(assignment.tipId)
        .then(result => setTip(result))
        .catch(console.error);
    }
  }, [assignment.tipId, fetchTipById]);

  const hasTip = !!tip;
  const hasSource = !!source;

  if (!hasTip && !hasSource) return null;

  return (
    <div className={clsx('grid grid-cols-2 gap-2 w-full', className)}>
      {hasSource && (
        <Card className='p-3'>
          <div className='flex items-start justify-between w-full'>
            <div className='flex flex-col flex-1'>
              <div className='flex items-center gap-1 mb-1'>
                <User className='h-4 w-4 mt-0.5 text-muted-foreground' />
                <span className='text-xs uppercase text-muted-foreground'>
                  Source
                </span>
              </div>

              <p className='text-sm font-medium text-left mb-1'>
                {(source.firstName &&
                  `${source.firstName} ${source.lastName}`) ||
                  'Unknown source'}
              </p>

              {source.company && (
                <div className='flex items-center gap-1 text-xs text-muted-foreground'>
                  <Briefcase className='h-3 w-3' />
                  <span>{source.company}</span>
                </div>
              )}
            </div>

            <ArrowUpRightSquare
              size={16}
              className='flex-shrink-0 mt-1 text-muted-foreground hover:text-primary cursor-pointer'
            />
          </div>
        </Card>
      )}

      {hasTip && (
        <Card className='p-3'>
          <div className='flex items-start justify-between w-full'>
            <div className='flex flex-col flex-1'>
              <div className='flex items-center gap-1 mb-1'>
                <FileText className='h-4 w-4 mt-0.5 text-muted-foreground' />
                <span className='text-xs uppercase text-muted-foreground'>
                  Tip
                </span>
              </div>

              <p className='text-sm font-medium text-left truncate max-w-[400px] mb-1'>
                {tip.title || 'Untitled tip'}
              </p>

              <p className='text-xs text-left text-muted-foreground'>
                From: {tip.sourceContact || 'Unknown source'}
              </p>
            </div>

            <ArrowUpRightSquare
              size={16}
              className='flex-shrink-0 mt-1 text-muted-foreground hover:text-primary cursor-pointer'
            />
          </div>
        </Card>
      )}
    </div>
  );
}
